import { cva } from 'class-variance-authority'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet'

import { AppSidebar } from '@/components/AppSidebar/'
import { BottomNav } from '@/components/BottomNav'
import { Breadcrumbs } from '@/components/Breadcrumbs'
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/elements/sidebar'
import { MobileHeader } from '@/components/MobileHeader'
import { cn } from '@/utils/helpers'

const layoutVariants = cva(
  'bg-background flex h-full flex-col overflow-y-auto px-4 pb-24 md:pb-12 sm:px-6 lg:px-8',
  {
    variants: {
      variant: {
        slim: 'max-w-6xl',
        full: 'w-full',
      },
    },
    defaultVariants: {
      variant: 'full',
    },
  },
)

/**
 * Layout component
 *
 * This component is the main wrapper for application pages. It provides the
 * sidebar, breadcrumbs, and main content area.
 *
 * @param {object} props
 * @param {string} [props.variant='default'] - The layout variant to use
 * @param {import('@/components/Breadcrumbs/Breadcrumbs').BreadcrumbItem[]} [props.breadcrumbs] Array of breadcrumb items
 * @param {React.ReactNode} props.children - The main content to display
 * @returns {import('react').JSX.Element} The layout component
 */
const Layout = ({ variant = 'default', breadcrumbs = [], children }) => {
  // set page title based on breadcrumbs
  const pageTitle =
    breadcrumbs.length > 0
      ? breadcrumbs
          .map((m) => (typeof m === 'string' ? m : m.title))
          .filter((title) => title.trim() !== '')
          .join(' › ')
      : null

  return (
    <SidebarProvider>
      <Helmet title={pageTitle} />
      <AppSidebar />
      <SidebarInset>
        <MobileHeader />
        <header className="hidden h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12 md:flex">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            {breadcrumbs?.length > 0 && <Breadcrumbs breadcrumbs={breadcrumbs} />}
          </div>
        </header>

        <section className={cn(layoutVariants({ variant }))}>{children}</section>
      </SidebarInset>
      <BottomNav />
    </SidebarProvider>
  )
}

Layout.propTypes = {
  variant: PropTypes.string,
  breadcrumbs: PropTypes.array,
  children: PropTypes.node.isRequired,
}

/**
 * Layout.Heading
 *
 * A heading component for displaying a page title with optional  content on the right.
 *
 * @param {object} props
 * @param {string} props.title - The heading title to display.
 * @param {string} [props.eyebrow] - Small uppercase kicker rendered above the title (e.g. "Module") to label what the title is.
 * @param {string} [props.subtitle] - Secondary line rendered below the title for supporting context.
 * @param {string} [props.className] - Additional CSS classes to apply.
 * @param {React.ReactNode} [props.children] - Action buttons or other content to display on the right.
 * @returns {React.ReactElement} The rendered heading with title and optional actions.
 */
Layout.Heading = function LayoutHeading({ title, eyebrow, subtitle, className, children }) {
  return (
    <section className={cn('flex w-full flex-wrap items-center justify-between gap-4', className)}>
      <div className="flex flex-col gap-0.5">
        {eyebrow && (
          <span className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
            {eyebrow}
          </span>
        )}
        <h1>{title}</h1>
        {subtitle && <p className="text-muted-foreground text-sm">{subtitle}</p>}
      </div>
      {children}
    </section>
  )
}

Layout.Heading.propTypes = {
  title: PropTypes.string.isRequired,
  eyebrow: PropTypes.string,
  subtitle: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
}

export default Layout
